import type { WatchStopHandle } from "vue"

import { uuidv7 } from "uuidv7"

import type { SharedDriveWatchMessageDatagram } from "~/models/polyschema/SharedDriveWatchMessageDatagram"
import type { SharedDriveWatchResponseDatagram } from "~/models/polyschema/SharedDriveWatchResponseDatagram"

import { GlobalComposable } from "~/classes/composable/GlobalComposable"
import { PropertyId, PropertySchema, PropertyStatus } from "~/classes/entity"
import { BaseFile } from "~/classes/file/BaseFile"
import { FileUserRole } from "~/classes/file/properties/FileUserRole"
import { StaticFile } from "~/classes/file/StaticFile"
import { sendSharedDriveDeleteRequests } from "~/classes/handlers/sharedDrive/delete"
import { sendSharedDriveFindRequests } from "~/classes/handlers/sharedDrive/find"
import { sendSharedDriveMembershipRequest } from "~/classes/handlers/sharedDrive/membership"
import { sendSelfSharedDrivesRequest } from "~/classes/handlers/user/shared-drives"

interface State {
  /** Mapping from shared drive id to info */
  all: Record<string, {
    home: StaticFile
    archive: StaticFile
  }>
  new: {
    dialogOpen: boolean
    name: string
    handle: string
    loading: boolean
  }
  current: {
    invite: {
      requestId: string
      email: string
      isLoading: boolean
    }
  }
  sharedDriveWatcher?: WatchStopHandle
}

class SharedDrives extends GlobalComposable<State> {
  override defaultState = (): State => {
    return {
      all: {},
      new: {
        dialogOpen: false,
        name: "",
        handle: "",
        loading: false,
      },
      current: {
        invite: {
          requestId: "",
          email: "",
          isLoading: false,
        },
      },
    }
  }

  override onCreate = () => {
    const { addInterfaceEventListener } = useEvents()
    const { currentSharedDriveHome } = useNavigation()
    addInterfaceEventListener("sharedDriveMembershipResponse", this.onSharedDriveMembershipResponse)
    addInterfaceEventListener("selfSharedDrivesResponse", this.onSelfSharedDrivesResponse)
    addInterfaceEventListener("sharedDriveFindResponse", this.onSharedDriveFindResponse)
    addInterfaceEventListener("sharedDriveWatchResponse", this.onSharedDriveWatchResponse)

    this.state.value.sharedDriveWatcher = watchImmediate(currentSharedDriveHome, this.onUpdatedCurrentSharedDrive)
  }

  override onDestroy = () => {
    const { removeInterfaceEventListener } = useEvents()
    removeInterfaceEventListener("sharedDriveMembershipResponse", this.onSharedDriveMembershipResponse)
    removeInterfaceEventListener("selfSharedDrivesResponse", this.onSelfSharedDrivesResponse)
    removeInterfaceEventListener("sharedDriveFindResponse", this.onSharedDriveFindResponse)
    removeInterfaceEventListener("sharedDriveWatchResponse", this.onSharedDriveWatchResponse)
    this.state.value.sharedDriveWatcher?.()
  }

  clearSharedDriveState = () => {
    this.state.value = this.defaultState()
  }

  resetSharedDriveCreationState = () => {
    this.state.value.new = {
      dialogOpen: false,
      name: "",
      handle: "",
      loading: false,
    }
  }

  resetSharedDriveInviteState = () => {
    this.state.value.current.invite = {
      requestId: "",
      email: "",
      isLoading: false,
    }
  }

  currentSharedDriveId = computed(() => useNavigation().currentSharedDriveHome.value?.rootSharedDriveId())
  currentSharedDriveInfo = computed(() => {
    const sharedDriveId = this.currentSharedDriveId.value
    if (isDefined(sharedDriveId))
      return this.state.value.all[sharedDriveId]
    return undefined
  })

  currentSharedDriveRole = computed(() => {
    const currentSharedDriveInfo = this.currentSharedDriveInfo.value
    if (!currentSharedDriveInfo)
      return undefined
    return currentSharedDriveInfo.home.impliedSelfRole()
  })

  /** Includes shared drives that I am a member of or a watcher of */
  allMySharedDrives = computed(() => {
    const { all } = this.state.value
    const { authenticatedUserId } = useAuthentication()

    const userId = authenticatedUserId()
    const mine: typeof all = {}
    if (!isDefined(userId))
      return mine

    for (const [id, info] of Object.entries(all))
      if (info.home.hasWatcher(userId) || info.home.impliedSelfRole().canView())
        mine[id] = info

    return mine
  })

  requestSelfSharedDrives = () => {
    const request: SelfSharedDrivesRequestDatagram = {
      requestId: generateWorkerRequestId(),
      offline: false,
    }
    sendSelfSharedDrivesRequest(request)
    return request
  }

  onSelfSharedDrivesResponse = async (
    response: SelfSharedDrivesResponseDatagram,
  ) => {
    const { subscribeToFolderUpdates } = useFileSystem()
    const { addEntityEventListener } = useEvents()
    const { removeEntityEventListener } = useEvents()

    if (response.sharedDrives === undefined)
      return this.error("Shared drives listing errored")
    const subscribe: Set<string> = new Set()

    for (const [sharedDriveId, roots] of Object.entries(response.sharedDrives)) {
      if (!roots)
        throw new Error("Malformed message")
      const [home, archive] = roots.map(f => new StaticFile(f))
      if (this.state.value.all[sharedDriveId]) {
        this.state.value.all[sharedDriveId].home.mergeWithNewer(home)
        this.state.value.all[sharedDriveId].archive.mergeWithNewer(archive)
      }
      else {
        this.state.value.all[sharedDriveId] = { home, archive }
        addEntityEventListener(
          "fileProperties",
          home.id,
          this.onSharedDriveHomeProperties,
        )
        addEntityEventListener(
          "fileProperties",
          archive.id,
          this.onSharedDriveArchiveProperties,
        )
        addEntityEventListener(
          "sharedDriveDeleted",
          sharedDriveId,
          this.onDeletedSharedDrive,
        )
        subscribe.add(home.id)
        subscribe.add(archive.id)
      }
    }

    // if this is not offline, remove any folders we had added when cached that are not
    // in the final response
    for (const [sharedDriveId, roots] of Object.entries(this.state.value.all)) {
      if (!isDefined(response.sharedDrives[sharedDriveId])) {
        delete this.state.value.all[sharedDriveId]
        const { home, archive } = roots
        this.debug("Removing shared drive from state", home, archive)
        removeEntityEventListener("fileProperties", home.id, this.onSharedDriveHomeProperties)
        removeEntityEventListener("fileProperties", archive.id, this.onSharedDriveArchiveProperties)
        removeEntityEventListener("sharedDriveDeleted", sharedDriveId, this.onDeletedSharedDrive)
        subscribe.delete(home.id)
        subscribe.delete(archive.id)
      }
    }

    if (len(subscribe))
      subscribeToFolderUpdates([...subscribe])
  }

  /**
   * TODO: in order to make this "offline" proof (which perhaps we don't want to do?), we would need to
   * do this creation in two stages -- first, we create the shared drive with a random handle that we
   * can guarantee basically doesn't exist, then we modify that handle to the one the user desires.
   * Alternatively, we can re-create the shared drive with a different handle automatically the way
   * we move creation tasks to the archive.
   */
  createSharedDrive = async () => {
    const { state: myself } = useSelf()
    const { addEntityEventListener } = useEvents()
    const { createSharedDriveWithDefaultProperties } = useCache()
    if (!myself.value.user)
      throw new Error("Not logged in")

    this.state.value.new.loading = true

    const sharedDrivePreHash = StaticFile.sharedDrivePreHashId()
    const sharedDriveId = await StaticFile.idFromPrehash(sharedDrivePreHash)
    const homePreHash = StaticFile.clientSharedHomePreHashId()
    const archivePreHash = StaticFile.clientSharedArchivePreHashId()

    this.warn("Using the user-generated handle, we need to use a random handle or change the way we create shared drives")

    addEntityEventListener(
      "sharedDriveCreated",
      sharedDriveId,
      this.onSharedDriveCreated,
    )
    return await createSharedDriveWithDefaultProperties(
      sharedDrivePreHash,
      homePreHash,
      archivePreHash,
      myself.value.user.id,
      this.state.value.new.name,
      this.state.value.new.handle,
    )
  }

  onSharedDriveCreated = (event: SharedDriveEvent, offline: boolean) => {
    const { navigateToSharedDrive } = useNavigation()
    const { removeEntityEventListener } = useEvents()

    if (!offline)
      removeEntityEventListener("sharedDriveCreated", event.sharedDriveId, this.onSharedDriveCreated)

    this.resetSharedDriveCreationState()

    const sharedDriveId = event.sharedDriveId
    const home = new StaticFile(event.home)
    const archive = new StaticFile(event.archive)

    this.onAddedToSharedDrive({ sharedDriveId, home, archive })

    // at the end, lets navigate to the shared drive
    return navigateToSharedDrive(home)
  }

  onAddedToSharedDrive = ({ sharedDriveId, home, archive }: {
    sharedDriveId: string
    home: StaticFile
    archive: StaticFile
  }) => {
    const { addEntityEventListener } = useEvents()
    const { subscribeToFolderUpdates } = useFileSystem()

    if (this.state.value.all[sharedDriveId]) {
      this.state.value.all[sharedDriveId].home.mergeWithNewer(home)
      this.state.value.all[sharedDriveId].archive.mergeWithNewer(archive)
    }
    else {
      // TODO: Once shared drive find requests have a selection, we should make sure to
      // retrieve our watcher property so that the UI can be sure of "why" we were added
      this.state.value.all[sharedDriveId] = { home, archive }
      sendSharedDriveFindRequests([{
        requestId: generateWorkerRequestId(),
        sharedDriveId,
        offline: false,
      }])
      addEntityEventListener(
        "fileProperties",
        home.id,
        this.onSharedDriveHomeProperties,
      )
      addEntityEventListener(
        "fileProperties",
        archive.id,
        this.onSharedDriveArchiveProperties,
      )
      addEntityEventListener(
        "sharedDriveDeleted",
        sharedDriveId,
        this.onDeletedSharedDrive,
      )
      subscribeToFolderUpdates([home.id, archive.id])
    }
  }

  onRemovedFromSharedDrive = (sharedDriveId: string) => {
    // TODO: unsubscribe from updates?

    const { removeEntityEventListener } = useEvents()
    const { navigateToSelfHome, currentFilePreview, currentRootFolder } = useNavigation()

    removeEntityEventListener("sharedDriveDeleted", sharedDriveId, this.onDeletedSharedDrive)

    if (this.state.value.all[sharedDriveId]) {
      const { home, archive } = this.state.value.all[sharedDriveId]
      removeEntityEventListener("fileProperties", home.id, this.onSharedDriveHomeProperties)
      removeEntityEventListener("fileProperties", archive.id, this.onSharedDriveArchiveProperties)
      delete this.state.value.all[sharedDriveId]
    }

    if (currentFilePreview.value?.rootSharedDriveId() === sharedDriveId)
      navigateToSelfHome()
    else if (currentRootFolder.value?.rootSharedDriveId() === sharedDriveId)
      navigateToSelfHome()
  }

  onSharedDriveHomeProperties = (event: FileEvent) => {
    const newHome = StaticFile.fromEvent(event)
    for (const [id, { home }] of Object.entries(this.state.value.all)) {
      if (home.id !== newHome.id)
        continue
      this.state.value.all[id]!.home.mergeWithNewer(newHome)
      return
    }
    this.error("Did not find shared drive home")
  }

  onSharedDriveArchiveProperties = (event: FileEvent) => {
    const newArchive = StaticFile.fromEvent(event)
    for (const [id, { archive }] of Object.entries(this.state.value.all)) {
      if (archive.id !== newArchive.id)
        continue
      this.state.value.all[id]!.archive.mergeWithNewer(newArchive)
      return
    }
    this.error("Did not find shared drive archive")
  }

  /**
   * When you navigate and the shared drive home changes. This watcher is attached
   * to the `currentSharedDriveHome` of the useNavigation state manager.
   */
  onUpdatedCurrentSharedDrive = (newSharedDriveHome?: ReactiveFile, oldSharedDriveHome?: ReactiveFile) => {
    const { fetchUserInfoIfNotCached } = useSavedUsers()

    if (newSharedDriveHome?.id === oldSharedDriveHome?.id)
      return

    // Use the data in the shared drive state as a source of truth
    // any user data we fetch we use the `useSavedUsers` composable
    // users that are

    if (!isDefined(newSharedDriveHome))
      return this.debug("Navigated out of shared drive", oldSharedDriveHome)

    const rootSharedDriveId = newSharedDriveHome.rootSharedDriveId()
    if (!isDefined(rootSharedDriveId))
      throw new Error("No way to get shared drive info without shared drive id")

    const newHome = this.state.value.all[rootSharedDriveId]?.home
    if (!newHome) {
      return this.error("No shared drive state found for id and fetching shared drive info not implemented yet", newHome)
    }

    const members = newHome.sharedDriveUserRoles()
    if (len(members) === 0)
      return this.warn("No members in shared drive")

    fetchUserInfoIfNotCached(Object.keys(members))
  }

  /**
   * Inviting a user requires we know their user id. In order to retrieve that we must get the
   * user's id via a find request before we can actually do the inviting.
   */
  inviteUserToCurrentSharedDrive = async () => {
    const { state: myself } = useSelf()
    const { notifyError } = useAlerts()
    const { isOffline } = useCache()
    const { findUserIdByEmail } = useSavedUsers()

    // TODO: allow for offline changes?
    if (isOffline())
      return notifyError("Offline mode", "You cannot edit shared drive permissions in offline mode")

    const sharedDriveId = this.currentSharedDriveId.value
    const sharedDriveInfo = this.currentSharedDriveInfo.value

    const current = this.state.value.current
    if (!current || !myself.value.user)
      return console.error("No current shared drive to invite to")
    if (!isDefined(sharedDriveId) || !isDefined(sharedDriveInfo))
      return console.error("No shared drive info found for current shared drive")
    const email = current.invite.email.trim()
    if (!email)
      return console.error("No email provided for invite")

    current.invite.isLoading = true
    let userId
    try {
      userId = await findUserIdByEmail(email)
    }
    catch {
      current.invite.isLoading = false
      return notifyError("User not found", "User not found with that email address")
    }

    // Updating the membership requires we create a fake shared drive object that only has
    // the user membership as a single property of the home and archive directories.
    // Note that its possible this user was removed and therefore adding / inviting them
    // would require a version increment.
    const existing = sharedDriveInfo.home.property(BaseFile.sharedDriveUserPropertyId(userId))
    const timestamp = uuidv7()
    const property = {
      propertyId: BaseFile.sharedDriveUserPropertyId(userId),
      schema: PropertySchema.RoleUuidValue,
      version: existing?.version ? existing.version + 1 : 0,
      author: myself.value.user.id,
      value: {
        type: PropertySchema.RoleUuidValue,
        role: FileUserRole.newViewer().inner(),
      },
      timestamp,
      status: PropertyStatus.StatusPending,
      sharedWithUserId: userId,
    } as FileProperty

    const home = StaticFile.blank(sharedDriveInfo.home.id)
    home.addProperty({ ...property, id: home.id })
    home.setModifiedAt(timestamp, myself.value.user.id)

    const archive = StaticFile.blank(sharedDriveInfo.archive.id)
    archive.addProperty({ ...property, id: archive.id })
    archive.setModifiedAt(timestamp, myself.value.user.id)

    // This could be a lot cleaner...
    this.state.value.current.invite.requestId = generateWorkerRequestId()
    const request: SharedDriveMembershipRequestDatagram = {
      requestId: this.state.value.current.invite.requestId,
      sharedDriveId,
      home: home.inner(),
      archive: archive.inner(),
      offline: false,
    }
    sendSharedDriveMembershipRequest(request)
  }

  /**
   * If you don't provide a sharedDriveId, it will use the current shared drive
   * in the navigation state.
   */
  editSharedDriveMemberPermissions = ({ userId, role, remove, sharedDriveId }: {
    userId: string
    sharedDriveId?: string
    role?: FileUserRole
    remove?: boolean
  }) => {
    const { state: myself } = useSelf()
    const { notifyError } = useAlerts()
    const { isOffline } = useCache()

    // TODO: allow for offline changes?
    if (isOffline())
      return notifyError("Offline mode", "You cannot edit shared drive permissions in offline mode")

    // Check if we are updating our own permissions, and error if we are an owner
    if (myself.value.user?.id === userId && this.currentSharedDriveRole.value?.isOwner())
      return notifyError(
        "Cannot remove yourself as owner",
        "You cannot remove yourself as owner of a shared drive. Please contact another owner to do this.",
      )

    sharedDriveId = sharedDriveId ?? this.currentSharedDriveId.value
    const sharedDriveInfo = this.state.value.all[sharedDriveId ?? ""]

    if (!isDefined(sharedDriveId) || !isDefined(sharedDriveInfo))
      throw new Error(`No shared drive state found for id: ${sharedDriveId}`)
    if (!myself.value.user)
      throw new Error("Not logged in")

    const property = clone(sharedDriveInfo.home.property(BaseFile.sharedDriveUserPropertyId(userId)))
    if (!isDefined(property))
      throw new Error("Cannot edit permissions for user that is not in shared drive")
    const timestamp = uuidv7()

    if (property.status !== PropertyStatus.StatusPending)
      property.version = property.version + 1
    if (remove)
      property.deleted = true
    else if (role)
      property.value = { type: PropertySchema.RoleUuidValue, role: role.inner() }
    else
      throw new Error("No role provided for editing permissions")

    property.status = PropertyStatus.StatusPending
    property.timestamp = timestamp

    const home = StaticFile.blank(sharedDriveInfo.home.id)
    home.addProperty(property)
    home.setModifiedAt(timestamp, myself.value.user.id)

    const archive = StaticFile.blank(sharedDriveInfo.archive.id)
    archive.addProperty(property)
    archive.setModifiedAt(timestamp, myself.value.user.id)

    this.state.value.current.invite.requestId = generateWorkerRequestId()

    const request: SharedDriveMembershipRequestDatagram = {
      requestId: this.state.value.current.invite.requestId,
      sharedDriveId,
      home: home.inner(),
      archive: archive.inner(),
      offline: false,
    }

    sendSharedDriveMembershipRequest(request)

    return request
  }

  /**
   * If you don't provide a sharedDriveId, it will use the current shared drive
   * in the navigation state.
   */
  editSharedDrivePublicPermissions = ({ role, remove, sharedDriveId }: {
    role?: FileUserRole
    remove?: boolean
    sharedDriveId?: string
  }) => {
    const { state: myself } = useSelf()
    const { notifyError } = useAlerts()
    const { isOffline } = useCache()

    // TODO: allow for offline changes?
    if (isOffline()) {
      notifyError("Offline mode", "You cannot edit shared drive permissions in offline mode")
      return
    }

    sharedDriveId = sharedDriveId ?? this.currentSharedDriveId.value
    const sharedDriveInfo = this.state.value.all[sharedDriveId ?? ""]

    if (!isDefined(sharedDriveId) || !isDefined(sharedDriveInfo))
      throw new Error(`No shared drive state found for id: ${sharedDriveId}`)
    if (!myself.value.user)
      throw new Error("Not logged in")

    const property = clone(sharedDriveInfo.home.property(PropertyId.FILE_PUBLIC_USER_ROLE))
    if (!isDefined(property))
      throw new Error("No existing public permissions to edit")
    const timestamp = uuidv7()

    if (property.status !== PropertyStatus.StatusPending)
      property.version = property.version + 1
    if (remove)
      property.value = { type: PropertySchema.RoleUuidValue, role: FileUserRole.newBase().inner() }
    else if (role)
      property.value = { type: PropertySchema.RoleUuidValue, role: role.inner() }
    else
      property.value = { type: PropertySchema.RoleUuidValue, role: FileUserRole.newViewer().inner() }

    property.status = PropertyStatus.StatusPending
    property.timestamp = timestamp

    const home = StaticFile.blank(sharedDriveInfo.home.id)
    home.addProperty(property)
    home.setModifiedAt(timestamp, myself.value.user.id)

    const archive = StaticFile.blank(sharedDriveInfo.archive.id)
    archive.addProperty(property)
    archive.setModifiedAt(timestamp, myself.value.user.id)

    this.state.value.current.invite.requestId = generateWorkerRequestId()

    const request: SharedDriveMembershipRequestDatagram = {
      requestId: this.state.value.current.invite.requestId,
      sharedDriveId,
      home: home.inner(),
      archive: archive.inner(),
      offline: false,
    }

    sendSharedDriveMembershipRequest(request)

    return request
  }

  leaveSharedDrive = (sharedDriveId: string) => {
    const { state: myself } = useSelf()
    const { notifyError } = useAlerts()
    const { isOffline } = useCache()

    const sharedDriveInfo = this.state.value.all[sharedDriveId]

    if (!isDefined(sharedDriveInfo))
      throw new Error(`No shared drive state found for id: ${sharedDriveId}`)
    if (!myself.value.user)
      throw new Error("Not logged in")

    const members = sharedDriveInfo.home.sharedDriveUserRoles()
    const owners = Object.values(members).map(m => m.role).filter(r => r.isOwner())
    const role = members[myself.value.user.id]?.role

    // Check if we are the only owner, and error if so (we must do a delete since we can't leave
    if (role?.isOwner() && owners.length <= 1) {
      notifyError(
        "Cannot leave as owner",
        "You cannot leave a shared drive since you are the only owner. Delete the shared drive or add an owner before leaving.",
      )
      return
    }

    const home = StaticFile.blank(sharedDriveInfo.home.id)
    const archive = StaticFile.blank(sharedDriveInfo.archive.id)

    const propertyId = BaseFile.sharedDriveUserPropertyId(myself.value.user.id)
    const property = clone(sharedDriveInfo.home.property(propertyId))
    const timestamp = uuidv7()

    if (!property)
      throw new Error("Shared drive info incomplete -- needs own membership")
    property.deleted = true
    property.timestamp = timestamp
    property.author = myself.value.user.id
    if (property.status !== PropertyStatus.StatusPending && !isOffline())
      property.version = property.version + 1
    property.status = PropertyStatus.StatusPending

    home.addProperty(property)
    home.setModifiedAt(timestamp, myself.value.user.id)

    archive.addProperty(property)
    archive.setModifiedAt(timestamp, myself.value.user.id)

    const request: SharedDriveMembershipRequestDatagram = {
      requestId: generateWorkerRequestId(),
      sharedDriveId,
      home: home.inner(),
      archive: archive.inner(),
      offline: false,
    }

    sendSharedDriveMembershipRequest(request)

    this.onRemovedFromSharedDrive(sharedDriveId)

    return request
  }

  deleteSharedDrive = (sharedDriveId: string) => {
    const { state: myself } = useSelf()
    const { notifyError } = useAlerts()

    const sharedDriveInfo = this.state.value.all[sharedDriveId]

    if (!isDefined(sharedDriveInfo))
      throw new Error(`No shared drive state found for id: ${sharedDriveId}`)
    if (!myself.value.user)
      throw new Error("Not logged in")

    const members = sharedDriveInfo.home.sharedDriveUserRoles()
    const role = members[myself.value.user.id]?.role

    // Check if we are the only owner, and error if so (we must do a delete since we can't leave)
    if (!role?.isOwner()) {
      notifyError(
        "Cannot delete this shared drive",
        "You cannot delete a shared drive unless you are an owner of it. Please contact an owner to delete it.",
      )
      return
    }

    const request: SharedDriveDeleteRequestDatagram = {
      requestId: generateWorkerRequestId(),
      sharedDriveId,
      offline: false,
    }

    sendSharedDriveDeleteRequests([request])

    this.onRemovedFromSharedDrive(sharedDriveId)

    return request
  }

  onDeletedSharedDrive = (event: SharedDriveEvent) => {
    this.onRemovedFromSharedDrive(event.sharedDriveId)
  }

  onSharedDriveMembershipResponse = (response: SharedDriveMembershipResponseDatagram) => {
    if (response.requestId === this.state.value.current?.invite.requestId)
      this.resetSharedDriveInviteState()

    if (response.error || !response.home || !response.archive)
      return console.warn("Can't update membership. request errored")

    const { sharedDriveId, home, archive } = response
    this.onSharedDriveMembershipUpdated({
      sharedDriveId,
      home: new StaticFile(home),
      archive: new StaticFile(archive),
    })
  }

  onSharedDriveFindResponse = (response: SharedDriveFindResponseDatagram) => {
    if (response.error || !response.sharedDriveId || !response.home || !response.archive)
      return console.warn("Can't add found shared drive. request errored")

    const { sharedDriveId, home, archive } = response

    if (!this.state.value.all[sharedDriveId]) {
      this.state.value.all[sharedDriveId] = { home: new StaticFile(home), archive: new StaticFile(archive) }
      this.debug("Added found shared drive to state", this.state.value.all[sharedDriveId])
    }
    else {
      this.state.value.all[sharedDriveId].home.mergeWithNewer(new StaticFile(home))
      this.state.value.all[sharedDriveId].archive.mergeWithNewer(new StaticFile(archive))
      this.debug("Updated found shared drive in state", this.state.value.all[sharedDriveId])
    }
  }

  onSharedDriveWatchResponse = (response: SharedDriveWatchResponseDatagram) => {
    if (response.error || !response.sharedDriveId || !response.home || !response.archive)
      return console.warn("Can't update watched shared drive. request errored")

    const { sharedDriveId, home, archive } = response
    return this.onAddedToSharedDrive({ sharedDriveId, home: new StaticFile(home), archive: new StaticFile(archive) })
  }

  onSharedDriveWatchMessage = (message: SharedDriveWatchMessageDatagram) => {
    const { sharedDriveId, home, archive } = message

    // if we don't have this shared drive already, we need to make a request for it, e.g.
    // if we watched this shared drive from another device
    if (this.state.value.all[sharedDriveId]) {
      this.state.value.all[sharedDriveId].home.mergeWithNewer(new StaticFile(home))
      this.state.value.all[sharedDriveId].archive.mergeWithNewer(new StaticFile(archive))
      this.debug("Updated watched shared drive in state", this.state.value.all[sharedDriveId])
    }
    else {
      console.debug("Received update for unwatched shared drive, requesting info", message)
      sendSharedDriveFindRequests([{
        requestId: generateWorkerRequestId(),
        sharedDriveId,
        offline: false,
      }])
    }
  }

  onSharedDriveMembershipUpdated = ({ sharedDriveId, home, archive }: {
    sharedDriveId: string
    home: StaticFile
    archive: StaticFile
  }) => {
    const { state: myself } = useSelf()

    if (!myself.value.user)
      return console.error("Received shared drive updated when not logged in")

    // Let's merge the properties if we have them, which will help us identify if we
    // are still a member of the shared drive or not
    if (this.state.value.all[sharedDriveId]) {
      this.state.value.all[sharedDriveId].home.mergeWithNewer(home)
      this.state.value.all[sharedDriveId].archive.mergeWithNewer(archive)
    }
    home = this.state.value.all[sharedDriveId]?.home ?? home
    archive = this.state.value.all[sharedDriveId]?.archive ?? archive

    // Check if we were removed from the shared drive
    // We can be removed either by being deleted as a member, or if the folder was
    // made private and we don't have any other access (e.g. public role)

    const role = home.impliedSelfRole()
    if (role.canView() && !this.state.value.all[sharedDriveId])
      return this.onAddedToSharedDrive({ sharedDriveId, home, archive })
    else if (!role.canView())
      return this.onRemovedFromSharedDrive(sharedDriveId)
  }
}

const sharedDrives = new SharedDrives("shared-drives", theme.colors.violet.hex)
export default function () {
  return sharedDrives.use()
}
